import {
  AnimatePresence,
  motion,
  PresenceContext,
  useReducedMotion,
} from 'motion/react'

import { DialogContent, DialogDescription, DialogTitle } from '@/components/ui'
import { GuideActions } from './GuideActions'
import { GuideProgress } from './GuideProgress'
import { type StepDirection, useGuideSteps } from './useGuideSteps'

const SLIDE_DISTANCE = 24
const STEP_SECONDS = 0.25

const slide = {
  enter: (direction: StepDirection) => ({
    opacity: 0,
    x: direction * SLIDE_DISTANCE,
  }),
  center: { opacity: 1, x: 0 },
  exit: (direction: StepDirection) => ({
    opacity: 0,
    x: direction * -SLIDE_DISTANCE,
  }),
}

interface UploadGuideContentProps {
  onFinish: () => void
}

// The guide can only be left from its last step, so every step is seen once.
export const UploadGuideContent = ({ onFinish }: UploadGuideContentProps) => {
  const guide = useGuideSteps()
  const holdUntilLastStep = (event: Event) => {
    if (!guide.isLastStep) event.preventDefault()
  }

  return (
    <DialogContent
      hideClose
      onEscapeKeyDown={holdUntilLastStep}
      onInteractOutside={holdUntilLastStep}
      className="max-w-2xl gap-0 overflow-hidden p-0"
    >
      <GuideScenePanel guide={guide} />
      <div className="flex flex-col gap-5 p-6 sm:p-8">
        <GuideProgress stepKeys={guide.stepKeys} stepIndex={guide.stepIndex} />
        <GuideText guide={guide} />
        <GuideActions
          isFirstStep={guide.stepIndex === 0}
          isLastStep={guide.isLastStep}
          onBack={guide.goBack}
          onNext={guide.goNext}
          onFinish={onFinish}
        />
      </div>
    </DialogContent>
  )
}

type Guide = ReturnType<typeof useGuideSteps>

const useStepTransition = () => {
  const reduceMotion = useReducedMotion()
  return { duration: reduceMotion ? 0 : STEP_SECONDS, ease: 'easeOut' as const }
}

const GuideScenePanel = ({ guide }: { guide: Guide }) => {
  const transition = useStepTransition()
  const { Scene } = guide.step
  return (
    <div
      aria-hidden
      className="relative h-56 overflow-hidden bg-blue-1 bg-[radial-gradient(ellipse_at_top,hsl(var(--brand)/0.14),transparent_70%)]"
    >
      <AnimatePresence initial={false} custom={guide.direction}>
        <motion.div
          key={guide.step.key}
          className="absolute inset-0"
          custom={guide.direction}
          variants={slide}
          initial="enter"
          animate="center"
          exit="exit"
          transition={transition}
        >
          {/* The panel skips its slide-in when the guide opens, but the scene's loop must still start. */}
          <PresenceContext.Provider value={null}>
            <Scene />
          </PresenceContext.Provider>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

const GuideText = ({ guide }: { guide: Guide }) => {
  const transition = useStepTransition()
  return (
    <div aria-live="polite" className="min-h-36 sm:min-h-24">
      <AnimatePresence mode="wait" initial={false} custom={guide.direction}>
        <motion.div
          key={guide.step.key}
          custom={guide.direction}
          variants={slide}
          initial="enter"
          animate="center"
          exit="exit"
          transition={transition}
        >
          <DialogTitle className="text-xl font-semibold tracking-tight text-blue-7 text-pretty">
            {guide.step.title}
          </DialogTitle>
          <DialogDescription className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
            {guide.step.description}
          </DialogDescription>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
